import {
  Area,
  ComposedChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import { CloudSun } from "lucide-react";
import { SectionPanel } from "@/components/ui/section-panel";
import {
  splitForecast,
  nowKeyOf,
  type OperationalLoopPoint,
} from "./operational-loop-data";
import {
  CHART_MARGIN,
  hoverHandlers,
  loopXAxisProps,
  loopYAxis,
  LOOP_STAGE_ACCENT,
  type HoverHandler,
} from "./chart-shared";
import { nowMarker } from "./NowMarker";
import { ForecastTooltip } from "./ForecastTooltip";

export type EnvironmentVisibility = {
  price: boolean;
  outdoorTemp: boolean;
  solar: boolean;
};

// 1. Vaikuttimet — the external drivers the loop reads before it reacts: spot price, outdoor
// temperature and solar radiation. `visible` narrows the card to the signals the KB's
// optimization services actually read for the selected equipment (OperationalLoopSection's
// focus, from `report.focus`); `null` shows all three. When nothing applies the card stays —
// empty with its reason — rather than disappearing: an absent card says nothing, an empty one
// says why.
export function EnvironmentChart({
  points,
  visible,
  emptyReason,
  unavailable,
  onHover,
}: {
  points: OperationalLoopPoint[];
  visible: EnvironmentVisibility | null;
  emptyReason?: string;
  unavailable?: string;
  onHover?: HoverHandler;
}) {
  const show = visible ?? { price: true, outdoorTemp: true, solar: true };
  const rows = splitForecast(points, [
    "spotPriceCtPerKwh",
    "outdoorTempC",
    "solarRadiationWm2",
  ]);
  const nowKey = nowKeyOf(points);
  const firstKey = rows[0]?.key ?? nowKey;
  // Marker must sit on a reserved (never-hidden) axis — solar is scale-only.
  const markerAxis = show.price ? "price" : "temp";
  const subtitle = [
    show.price && "sähkön spot-hinta (snt/kWh)",
    show.outdoorTemp && "ulkolämpötila (°C)",
    show.solar && "auringon säteily (W/m²)",
  ]
    .filter(Boolean)
    .join(", ");
  const nothing = !show.price && !show.outdoorTemp && !show.solar;

  return (
    <SectionPanel
      icon={CloudSun}
      title="Ulkoiset vaikuttimet"
      subtitle={
        nothing
          ? "Ei tämän laitteen optimoinnin syöte"
          : `${subtitle.charAt(0).toUpperCase()}${subtitle.slice(1)}`
      }
      accentColor={LOOP_STAGE_ACCENT.environment}
    >
      {nothing ? (
        <p className="text-xs text-muted-foreground">{emptyReason}</p>
      ) : unavailable ? (
        <p className="text-xs text-muted-foreground">{unavailable}</p>
      ) : (
        <div className="h-40 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={rows}
              margin={CHART_MARGIN}
              syncId="operational-loop"
              {...hoverHandlers(onHover)}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="var(--border)"
                strokeOpacity={0.5}
              />
              <XAxis {...loopXAxisProps(rows)} />
              <YAxis {...loopYAxis("left", "price", { labeled: show.price })} />
              <YAxis
                {...loopYAxis("right", "temp", { labeled: show.outdoorTemp })}
              />
              {/* Solar radiation shares no plausible scale with price or temperature (0-1000 W/m²
                  vs. snt/kWh or °C) — unlabeled third axis; the value still surfaces in the
                  tooltip. Does not reserve width (hide), so NYT never binds to it. */}
              <YAxis yAxisId="solar" hide domain={[0, "dataMax"]} />
              <Tooltip content={<ForecastTooltip />} />

              {nowMarker({ x: nowKey, firstX: firstKey, yAxisId: markerAxis })}

              {show.price && (
                <Area
                  yAxisId="price"
                  type="stepAfter"
                  name="Spot-hinta (Historia)"
                  dataKey="spotPriceCtPerKwhPast"
                  fill="#3b82f6"
                  fillOpacity={0.12}
                  stroke="#3b82f6"
                  strokeWidth={2}
                />
              )}
              {show.price && (
                <Area
                  yAxisId="price"
                  type="stepAfter"
                  name="Spot-hinta (Ennuste)"
                  dataKey="spotPriceCtPerKwhFuture"
                  fill="#3b82f6"
                  fillOpacity={0.05}
                  stroke="#3b82f6"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                />
              )}

              {show.outdoorTemp && (
                <Line
                  yAxisId="temp"
                  type="stepAfter"
                  name="Ulkolämpötila (Historia)"
                  dataKey="outdoorTempCPast"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={false}
                />
              )}
              {show.outdoorTemp && (
                <Line
                  yAxisId="temp"
                  type="stepAfter"
                  name="Ulkolämpötila (Ennuste)"
                  dataKey="outdoorTempCFuture"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}

              {show.solar && (
                <Line
                  yAxisId="solar"
                  type="stepAfter"
                  name="Auringon säteily (Historia)"
                  dataKey="solarRadiationWm2Past"
                  stroke="#eab308"
                  strokeWidth={2}
                  dot={false}
                />
              )}
              {show.solar && (
                <Line
                  yAxisId="solar"
                  type="stepAfter"
                  name="Auringon säteily (Ennuste)"
                  dataKey="solarRadiationWm2Future"
                  stroke="#eab308"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionPanel>
  );
}
